import React from "react";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Radio from "@material-ui/core/Radio";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import LastPageIcon from "@material-ui/icons/LastPage";

const useStyles1 = makeStyles((theme) => ({
	root: {
		flexShrink: 0,
		marginLeft: theme.spacing(2.5),
	},
}));

function TablePaginationActions(props) {
	const classes = useStyles1();
	const theme = useTheme();
	const { count, page, rowsPerPage, onChangePage } = props;

	const handleFirstPageButtonClick = (event) => {
		onChangePage(event, 0);
	};

	const handleBackButtonClick = (event) => {
		onChangePage(event, page - 1);
	};

	const handleNextButtonClick = (event) => {
		onChangePage(event, page + 1);
	};

	const handleLastPageButtonClick = (event) => {
		onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
	};

	return (
		<div className={classes.root}>
			<IconButton
				onClick={handleFirstPageButtonClick}
				disabled={page === 0}
				aria-label="first page">
				{theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
			</IconButton>
			<IconButton
				onClick={handleBackButtonClick}
				disabled={page === 0}
				aria-label="previous page">
				{theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
			</IconButton>
			<IconButton
				onClick={handleNextButtonClick}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
				aria-label="next page">
				{theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
			</IconButton>
			<IconButton
				onClick={handleLastPageButtonClick}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
				aria-label="last page">
				{theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
			</IconButton>
		</div>
	);
}

TablePaginationActions.propTypes = {
	count: PropTypes.number.isRequired,
	onChangePage: PropTypes.func.isRequired,
	page: PropTypes.number.isRequired,
	rowsPerPage: PropTypes.number.isRequired,
};

const useStyles2 = makeStyles({
	table: {
		minWidth: 500,
		"& th": {
			fontWeight: 600
		}
	},
	paper: {
		boxShadow: "0 0 2px 0 rgba(0,0,0,.5)",
	},
});

export default function VendorDataTable(props) {
	const { data, selectedVendor, setSelectedVendor, updateFeeVendor, type } = props;
	const classes = useStyles2();
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(5);

  // ---- sort: # Funded Deals (desc), then Vendor ID (desc) ----
  const sortedData = React.useMemo(() => {
    const list = Array.isArray(data) ? [...data] : [];
    return list.sort((a, b) => {
      const aFunded = Number(a.number_of_fund ?? a.funded_deals ?? 0);
      const bFunded = Number(b.number_of_fund ?? b.funded_deals ?? 0);
      if (bFunded !== aFunded) return bFunded - aFunded; // desc by funded
      const aId = (a.short_id || "").toString();
      const bId = (b.short_id || "").toString();
      // desc by Vendor ID (alphanumeric-aware)
      return bId.localeCompare(aId, undefined, { numeric: true, sensitivity: "base" });
    });
  }, [data]);

	const appendAddress = (vendorData) => {
		if (vendorData.address) {
			return vendorData.address;
		}
		let vendorAddress = "";
		if (vendorData.street) {
			vendorAddress = `${vendorData.street}`;
		}
		if (vendorData.city) {
			vendorAddress = `${vendorAddress}, ${vendorData.city}`;
		}
		if (vendorData.state) {
			vendorAddress = `${vendorAddress}, ${vendorData.state}`;
		}
		if (vendorData.zip_code) {
			vendorAddress = `${vendorAddress} - ${vendorData.zip_code}, `;
		}
		
		return vendorAddress;
	}

	const emptyRows =
		rowsPerPage - Math.min(rowsPerPage, data?.length - page * rowsPerPage);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};
	const pageData =
    rowsPerPage > 0
      ? sortedData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : sortedData;

	return (
		<TableContainer className={classes.paper}>
			<Table className={classes.table} size="small">
				<TableHead>
					<TableRow>
						<TableCell>Select</TableCell>
						<TableCell>Vendor ID</TableCell>
						<TableCell align="left">Vendor name</TableCell>
						<TableCell align="left"># Funded Deals</TableCell>
						<TableCell align="left">Address</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{pageData?.map((row, index) => (
						<TableRow key={index}>
							<TableCell component="th" scope="row">
								<Radio
									checked={row.id === selectedVendor?.id}
									onChange={() => {
										const vend = {
											id: row.id,
											short_id: row.short_id,
											name: row.name,
											address: appendAddress(row),
											street: row.street ?? "",
											city: row.city ?? "",
											state: row.state ?? "",
											zip_code: row.zip_code ?? "",
											country: row.country ?? "",
										};
										setSelectedVendor(vend);
										if (type === "fee") {
											updateFeeVendor(vend);
										}
									}}
									value={row.id}
									color="primary"
									size="small"
									style={{ padding: 2 }}
								/>
							</TableCell>
							<TableCell align="left">{row.short_id}</TableCell>
							<TableCell align="left">{row.name}</TableCell>
							<TableCell align="left">{Number(row.number_of_fund ?? row.funded_deals ?? 0)}</TableCell>
							<TableCell align="left">{row.address}</TableCell>
						</TableRow>
					))}

					{emptyRows > 0 && (
						<TableRow style={{ height: 53 * emptyRows }}>
							<TableCell colSpan={6} />
						</TableRow>
					)}
				</TableBody>
				<TableFooter>
					<TableRow>
						<TablePagination
							rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
							colSpan={3}
							count={sortedData?.length || 0}
							rowsPerPage={rowsPerPage}
							page={page}
							SelectProps={{
								inputProps: { "aria-label": "rows per page" },
								native: true,
							}}
							onChangePage={handleChangePage}
							onChangeRowsPerPage={handleChangeRowsPerPage}
							ActionsComponent={TablePaginationActions}
						/>
					</TableRow>
				</TableFooter>
			</Table>
		</TableContainer>
	);
}
